/**
 * Ловушка Refine: `result.data` существует ДО загрузки — пустым объектом.
 *
 * То есть `query.result.data` не `undefined`, как ждёт тип, а `{}`, и первое
 * же обращение к полю ответа (`list.wb.available`, `product.cards.length`)
 * роняет ВЕСЬ экран белым листом ещё до того, как придёт ответ. Тип здесь не
 * спасает: он обещает готовый объект, а приезжает пустой.
 *
 * Поймано на бою 08.09.26 разделом «Короба»: экран падал с
 * `Cannot read properties of undefined (reading 'available')`. Экраны, что
 * старше, делают ту же проверку внутри себя (`Array.isArray(loaded.cards)` в
 * карточке товара, `Array.isArray(loaded.lines)` в карточке короба) — здесь
 * она вынесена, потому что проверить её можно только вынесенной: DOM-тестов в
 * проекте нет, и сторожем этого класса ошибок может быть только чистая
 * функция.
 */

/**
 * Ответ, у которого ДЕЙСТВИТЕЛЬНО приехало тело, или `null`.
 *
 * Признак — массив в названном поле: он есть у каждого нашего списочного
 * ответа (`rows`, `cards`, `lines`, `products`) и его заведомо нет у пустой
 * заглушки Refine. Проверяется именно массив, а не наличие ключа: `{rows: 0}`
 * прошёл бы проверку на ключ и упал бы на `.map`.
 */
export function pageData<T, K extends keyof T>(
  data: T | undefined,
  key: K,
): T | null {
  if (!data || typeof data !== "object") return null;
  return Array.isArray((data as T)[key]) ? data : null;
}

/**
 * То же для ответа БЕЗ списка внутри — карточки одного объекта.
 *
 * Признак здесь ЧИСЛО в названном поле: у карточки места (`place_id`) массива
 * нет вовсе, и `pageData` считал бы её вечно незагруженной — экран так и стоял
 * на «Открываю место…» (поймано визуальной проверкой на бою 10.09.26).
 *
 * Ноль — законное значение и загрузку не отменяет, а `NaN` отменяет: пришедшее
 * из ответа число обязано быть числом, которым можно адресовать.
 */
export function pageOne<T, K extends keyof T>(
  data: T | undefined,
  key: K,
): T | null {
  if (!data || typeof data !== "object") return null;
  const value = (data as T)[key];
  return typeof value === "number" && !Number.isNaN(value) ? data : null;
}
